import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { Badge, Banner, Card, CardBody, EmptyState } from '@/ui'
import { isRunLive, type ProcessState, type RunView } from '../projection'
import { issuedNotIn, runFigures, sampleCandidates } from '../model/rules'
import { ProcessScreen } from '../components/ProcessScreen'
import { BalancePanel, RunStatusBadge } from '../components/RunCard'
import { StreamBadge, SyncBadges } from '../components/bits'
import { stepName } from '../components/labels'
import { useDraftState } from '../useProcess'
import { CleanoutSection } from './run/CleanoutSection'
import { WeighInSection } from './run/WeighInSection'
import { RunningSection } from './run/RunningSection'
import { WeighOutSection } from './run/WeighOutSection'
import { ClosedSection } from './run/ClosedSection'
import { emptyRunDraft, type RunDraft } from './run/draft'

type Tab = 'cleanout' | 'in' | 'run' | 'out'

/** One machine run, from cleanout to close (F6.2 – F6.5), then its result (F6.6-e). */
export function RunPage() {
  const { runId = '' } = useParams()
  return (
    <ProcessScreen title="process.run.title" backTo="/m/process">
      {(state) => {
        const run = state.runs.get(runId)
        return run ? <Run key={run.id} state={state} run={run} /> : <RunMissing />
      }}
    </ProcessScreen>
  )
}

function RunMissing() {
  const { t } = useI18n()
  return <EmptyState title={t('process.run.missing')} description={t('process.run.missingBody')} />
}

function firstTab(run: RunView, draft: RunDraft): Tab {
  if (!run.cleanout_confirmed_at) return 'cleanout'
  if (draft.stopped_at) return 'out'
  if (run.inputs.length === 0 || issuedNotIn(run).length > 0) return 'in'
  return 'run'
}

function Run({ state, run }: { state: ProcessState; run: RunView }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const actor = useActor()
  const [draft, update, loaded] = useDraftState<RunDraft>(
    `process.run:${run.id}`,
    'process.run',
    emptyRunDraft,
    actor.user_id,
  )
  const [tab, setTab] = useState<Tab | null>(null)
  const machine = state.machines.get(run.equipment_id)
  const live = isRunLive(run.status)
  const fig = runFigures(run, state.steps.get(run.step_code))
  if (!loaded) return null
  const current = tab ?? firstTab(run, draft)
  const tabs: Array<{ key: Tab; label: MessageKey; done: boolean; disabled: boolean }> = [
    { key: 'cleanout', label: 'process.tab.cleanout', done: !!run.cleanout_confirmed_at, disabled: false },
    { key: 'in', label: 'process.tab.in', done: issuedNotIn(run).length === 0 && run.inputs.length > 0, disabled: false },
    { key: 'run', label: 'process.tab.run', done: !!draft.stopped_at, disabled: false },
    { key: 'out', label: 'process.tab.out', done: false, disabled: false },
  ]
  const toSample = sampleCandidates(state).filter((c) => c.run?.id === run.id)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-1.5 py-3">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[20px] font-bold">{machine?.code ?? '—'}</span>
            <span className="text-[16px]">{stepName(state, run.step_code, locale)}</span>
          </div>
          <div className="font-mono text-[13px] text-muted">{run.code}</div>
          <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
            <RunStatusBadge run={run} />
            <StreamBadge stream={run.stream} />
            {run.farmer_code && <Badge>{run.farmer_code}</Badge>}
            {run.crop_name && <span className="text-muted">{run.crop_name}</span>}
            {run.season_code && <span className="font-mono text-[12px] text-muted">{run.season_code}</span>}
            <SyncBadges sync={run.sync} />
          </div>
          <div className="text-[13px] text-muted tabular">
            {t('process.bagsIn', { n: run.inputs.length })} · {fmt.weight(fig.input_g)}
            {run.started_at && ` · ${t('process.run.startedAt', { time: fmt.time(run.started_at) })}`}
          </div>
        </CardBody>
      </Card>

      {run.mixing_reason && (
        <Banner tone="error" title={t('process.mixing.reportedTitle')}>
          {t('process.mixing.reportedBody')}
        </Banner>
      )}
      {run.sync.error && <Banner tone="error">{t('process.run.syncError')}</Banner>}

      {!live ? (
        <>
          <ClosedSection state={state} run={run} />
          {toSample.length > 0 && (
            <Banner
              tone="info"
              action={
                <Link
                  to={`/m/process/qc-sample?unit=${encodeURIComponent(toSample[0]!.unit_id)}`}
                  className="font-mono text-[12px] font-bold uppercase underline"
                >
                  {t('process.qc.title')}
                </Link>
              }
            >
              {t('process.run.sampleNow')}
            </Banner>
          )}
          {run.status !== 'CANCELLED' && <BalancePanel state={state} run={run} />}
        </>
      ) : (
        <>
          <nav className="grid grid-cols-4 gap-1 rounded-chip border border-rule bg-paper-sunk p-1" aria-label={t('process.run.title')}>
            {tabs.map((x) => (
              <button
                key={x.key}
                type="button"
                disabled={x.disabled}
                aria-current={current === x.key ? 'step' : undefined}
                onClick={() => setTab(x.key)}
                className={cn(
                  'min-h-touch rounded-chip px-1 text-[12px] font-bold leading-tight transition-colors',
                  current === x.key ? 'bg-ink text-paper' : 'text-ink',
                  x.done && current !== x.key && 'text-ok',
                )}
              >
                {t(x.label)}
                {x.done && ' ✓'}
              </button>
            ))}
          </nav>
          {current === 'cleanout' && <CleanoutSection state={state} run={run} onDone={() => setTab('in')} />}
          {current === 'in' && <WeighInSection state={state} run={run} onGoCleanout={() => setTab('cleanout')} />}
          {current === 'run' && (
            <RunningSection state={state} run={run} draft={draft} update={update} onStopped={() => setTab('out')} />
          )}
          {current === 'out' && <WeighOutSection state={state} run={run} draft={draft} update={update} />}
        </>
      )}
    </div>
  )
}
